<script setup lang="ts">
import { computed, shallowRef, watch } from "vue"

import { ICON } from "@/lib/icons"
import type { ArchiveDispositionInput, ItemDispositionType } from "@/types/models"

const props = withDefaults(
  defineProps<{
    open: boolean
    itemName: string
    loading?: boolean
    errors?: Record<string, string>
  }>(),
  { errors: () => ({}) },
)

const emit = defineEmits<{
  "update:open": [value: boolean]
  confirm: [disposition: ArchiveDispositionInput]
}>()

const disposition = shallowRef<ItemDispositionType>("donated")
const date = shallowRef(today())
const counterparty = shallowRef("")
const notes = shallowRef("")
const amount = shallowRef("")
const currency = shallowRef("CAD")

const dispositionOptions = [
  { label: "Donated", value: "donated" },
  { label: "Sold", value: "sold" },
  { label: "Lost", value: "lost" },
  { label: "Discarded", value: "discarded" },
  { label: "Other", value: "other" },
]

const sold = computed(() => disposition.value === "sold")
const disabled = computed(
  () =>
    !disposition.value ||
    !date.value ||
    (sold.value && Boolean(amount.value) !== Boolean(currency.value)),
)

watch(
  () => props.open,
  (open) => {
    if (!open) return
    disposition.value = "donated"
    date.value = today()
    counterparty.value = ""
    notes.value = ""
    amount.value = ""
    currency.value = "CAD"
  },
)

function submit() {
  if (disabled.value) return
  emit("confirm", {
    type: disposition.value,
    date: date.value,
    counterparty: counterparty.value.trim() || null,
    notes: notes.value.trim() || null,
    amount: sold.value ? amount.value.trim() || null : null,
    currency: sold.value ? currency.value.trim().toUpperCase() || null : null,
  })
}

function today() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
}
</script>

<template>
  <UModal :open="open" @update:open="emit('update:open', $event)">
    <template #header>Archive {{ itemName }}?</template>
    <template #body>
      <form class="flex flex-col gap-4" @submit.prevent="submit">
        <UAlert
          v-if="Object.keys(errors).length"
          color="error"
          :icon="ICON.error"
          :description="Object.values(errors).join(' ')"
        />
        <UFormField label="Disposition" required>
          <USelect v-model="disposition" :items="dispositionOptions" value-key="value" />
        </UFormField>
        <UFormField label="Date" required>
          <UInput v-model="date" data-date type="date" class="w-full" />
        </UFormField>
        <UFormField :label="sold ? 'Buyer' : 'Recipient or counterparty'">
          <UInput v-model="counterparty" data-counterparty class="w-full" />
        </UFormField>
        <div v-if="sold" class="grid grid-cols-2 gap-4">
          <UFormField label="Amount">
            <UInput v-model="amount" data-amount inputmode="decimal" class="w-full" />
          </UFormField>
          <UFormField label="Currency">
            <UInput v-model="currency" data-currency maxlength="3" class="w-full" />
          </UFormField>
        </div>
        <UFormField label="Notes">
          <UTextarea v-model="notes" data-notes class="w-full" />
        </UFormField>
        <div class="flex justify-end gap-4">
          <UButton
            label="Cancel"
            color="neutral"
            variant="outline"
            @click="emit('update:open', false)"
          />
          <UButton
            type="submit"
            label="Archive"
            :icon="ICON.archive"
            :loading="loading"
            :disabled="disabled"
          />
        </div>
      </form>
    </template>
  </UModal>
</template>
